<script setup lang="ts">
interface Props {
  media: Media[]
  limit?: number
  /** which `limit` images the wall shows: a seeded draw, or the first ones */
  randomizePreview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  limit: 8,
  randomizePreview: true,
})

const lightboxOpen = ref(false)
const activeIndex = ref(0)
const lightboxView = ref<'carousel' | 'grid'>('carousel')

const hasMore = computed(() => props.media.length > previewMedia.value.length)

/** tiles per row, wide and narrow — matches the widths on the tile wrapper */
const COLUMNS = 4
const COLUMNS_BELOW_MD = 3
/** how many times as tall as it is wide a slot is — the template's aspect */
const TILE_HEIGHT = 1
const SLOT_ASPECT = `1 / ${TILE_HEIGHT}`

/**
 * Two neighbouring images are free to overlap and free to sit apart, but never
 * to merely touch: edges this close read as a failed alignment rather than a
 * decision. The sheet marks that "niet aansluiten".
 */
const MIN_DISTANCE_PX = 10

/** width assumed for the wall until it has been measured — SSR and first paint */
const ASSUMED_WALL_WIDTH = 1200
/** and the same for --unit--horizontal, which is the desktop line height */
const ASSUMED_UNIT = 28

/**
 * Sizes and offsets are both counted in units, in quarters of one, so neither
 * ever lands on a number the design system doesn't already have. Two units
 * either way is as far as both go.
 */
const STEP = 0.25
const MAX_UNITS = 2

const ladder = (max: number, step: number) => {
  const values: number[] = []
  for (let value = -max; value <= max + step / 2; value += step) {
    values.push(Math.round(value / step) * step)
  }
  return values
}

/** an image is drawn at its own size, give or take two units */
const SIZES = ladder(MAX_UNITS, STEP)

/**
 * Offsets are counted the same way but skip a whole unit either side: that is
 * the layout grid itself, and an image parked exactly on it reads as alignment
 * rather than as a decision. .75 and 1.25 are the way past it.
 */
const OFFSETS = ladder(MAX_UNITS, STEP).filter(value => Math.abs(value) !== 1)

/** every position an image may take, ordered by how far it sits from the middle */
const POSITIONS: [number, number][] = OFFSETS
  .flatMap(x => OFFSETS.map(y => [x, y] as [number, number]))
  .sort((a, b) => Math.max(...a.map(Math.abs)) - Math.max(...b.map(Math.abs)))

const wall = useTemplateRef<HTMLElement>('wall')
const { width: wallWidth } = useElementSize(wall)

// The tile wrapper drops to a third of the wall in CSS; the placement has to be
// told. Matches the "md — 768px" breakpoint the units switch at (unit.css).
// Held at the wide count until mount so the wall hydrates as the server drew it,
// and settles once, together with the measured unit below.
const mounted = useMounted()
const belowMd = useBreakpoints({ md: 768 }).smaller('md')
const columns = computed(() => (mounted.value && belowMd.value ? COLUMNS_BELOW_MD : COLUMNS))

// The style below spends units through calc(), but the placement has to do
// arithmetic with them, so the value is read back off the wall. It only moves
// with the breakpoint, which the wall's own width is a fair cue for.
const unitPx = ref(ASSUMED_UNIT)
watch(wallWidth, () => {
  const measured = wall.value && Number.parseFloat(getComputedStyle(wall.value).getPropertyValue('--unit--horizontal'))
  if (measured) { unitPx.value = measured }
}, { immediate: true })

// The placement works in tile widths, so both lengths are expressed in that
// unit: a tile is a quarter of the wall.
const tileWidth = computed(() => (wallWidth.value || ASSUMED_WALL_WIDTH) / columns.value)
const unit = computed(() => unitPx.value / tileWidth.value)
const minDistance = computed(() => MIN_DISTANCE_PX / tileWidth.value)

/**
 * mulberry32 — the wall is rolled from a single seed rather than from bare
 * Math.random() calls, so the same seed always lays out the same wall.
 */
const randomFrom = (seed: number) => {
  let state = seed
  return () => {
    state = (state + 0x6D2B79F5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// Rolled once per wall, on whichever side renders first. Nuxt carries the value
// into the payload, so hydration rebuilds the wall the server sent instead of
// rolling a second, different one and shuffling every tile on load.
const seed = useState(`media-preview-${useId()}`, () => Math.floor(Math.random() * 2 ** 31))

/**
 * The wall is drawn in whole rows, so the limit is rounded down to one: the
 * default 8 is two rows of four, and two rows of three below md. A last row
 * left short because that is all the media there is stays short — the count
 * is rounded, never the library.
 */
const previewLimit = computed(() => Math.floor(props.limit / columns.value) * columns.value || props.limit)

/**
 * Which images make the wall is a draw of its own, off the same seed but its own
 * stream, so turning the draw off leaves the placement of the tiles untouched.
 * Fisher-Yates, stopped after `limit` — the rest of the order is never looked at.
 */
const previewMedia = computed(() => {
  if (!props.randomizePreview) { return props.media.slice(0, previewLimit.value) }

  const random = randomFrom(seed.value ^ 0x9E3779B9)
  const pool = [...props.media]
  const count = Math.min(previewLimit.value, pool.length)

  for (let index = 0; index < count; index++) {
    const pick = index + Math.floor(random() * (pool.length - index))
    ;[pool[index], pool[pick]] = [pool[pick]!, pool[index]!]
  }

  return pool.slice(0, count)
})

interface Box { left: number, right: number, top: number, bottom: number }

/** Space between two spans on one axis — negative is how deep they overlap. */
const spanGap = (aMin: number, aMax: number, bMin: number, bMax: number) =>
  Math.max(bMin - aMax, aMin - bMax)

/**
 * "Niet aansluiten": a pair is fine when it clears on either axis, and fine when
 * it overlaps on both — anything shallower than `limit` is the in-between.
 */
const isTouching = (a: Box, b: Box, limit: number) => {
  const x = spanGap(a.left, a.right, b.left, b.right)
  const y = spanGap(a.top, a.bottom, b.top, b.bottom)

  if (Math.max(x, y) >= limit) { return false }
  return x > -limit || y > -limit
}

const layout = computed(() => {
  const placed: Box[] = []
  const limit = minDistance.value
  const step = unit.value
  const cols = columns.value
  const random = randomFrom(seed.value)

  const tiles = previewMedia.value.map((item, index) => {
    const size = SIZES[Math.floor(random() * SIZES.length)]!
    const column = index % cols
    const row = Math.floor(index / cols)

    // object-contain fits the image inside its slot, and that fitted rectangle
    // is the image's own size — for a document with no preview image, the sheet
    // standing in for it brings a ratio of its own. The units go on whichever
    // of its edges meets the slot; the other edge follows from the aspect
    // ratio, so the image is sized rather than stretched.
    const ratio = mediaAspectRatio(item)
    const isWide = ratio >= 1 / TILE_HEIGHT
    const baseWidth = isWide ? 1 : TILE_HEIGHT * ratio
    const baseHeight = isWide ? 1 / ratio : TILE_HEIGHT
    const growWidth = isWide ? size : size * ratio
    const growHeight = isWide ? size / ratio : size

    const width = baseWidth + growWidth * step
    const height = baseHeight + growHeight * step

    const centerX = column + 0.5
    const centerY = row * TILE_HEIGHT + TILE_HEIGHT / 2

    const boxAt = ([x, y]: [number, number]): Box => ({
      left: centerX + x * step - width / 2,
      right: centerX + x * step + width / 2,
      top: centerY + y * step - height / 2,
      bottom: centerY + y * step + height / 2,
    })

    // Every tile already on the wall counts, not just the neighbouring ones: two
    // units is far enough that a tile can reach the one diagonally above it.
    // Leaning off the wall doesn't count as clearing.
    const clears = (position: [number, number]) => {
      const box = boxAt(position)
      return box.left >= -0.001 && box.right <= cols + 0.001
        && placed.every(other => !isTouching(box, other, limit))
    }

    // Images lean into each other, not off the wall, so the outer columns roll
    // their offset inwards.
    const inwards = column === 0 ? 1 : column === cols - 1 ? -1 : 0
    const offset = () => OFFSETS[Math.floor(random() * OFFSETS.length)]!
    const rolledX = inwards === 0 ? offset() : inwards * Math.abs(offset())
    const rolledY = offset()

    // Positions are tried nearest the rolled one first, ties going to whichever
    // sits closest to the middle, so a tile that has to move moves as little as
    // possible. Falls back to the roll when the wall is too crowded for any
    // position to clear — one aligned pair beats a hole in the wall.
    const rolled: [number, number] = [rolledX, rolledY]
    const fromRoll = ([x, y]: [number, number]) => Math.max(Math.abs(x - rolledX), Math.abs(y - rolledY))
    const [x, y] = [...POSITIONS].sort((a, b) => fromRoll(a) - fromRoll(b)).find(clears) ?? rolled

    placed.push(boxAt([x, y]))

    return {
      item,
      // The lightbox is handed the whole of `media`, so a tile points at its
      // place there rather than at its place in the draw.
      index: props.media.indexOf(item),
      // The tile is the image, not the slot around it, so hovering the gap
      // beside a letterboxed image belongs to whatever is actually under the
      // cursor — which is what makes bringing the hovered tile to the front
      // read correctly where two of them overlap.
      // Width is a share of the slot's width; the height comes from the image's
      // own ratio rather than from a second share of the slot's height, so the
      // box is exactly the rectangle object-contain fits the image into — the
      // same width/height the placement above works with, and independent of
      // whatever aspect the slot happens to have.
      width: `calc(${baseWidth * 100}% + var(--unit--horizontal) * ${growWidth})`,
      ratio,
      transform: `translate(calc(var(--unit--horizontal) * ${x}), calc(var(--unit--horizontal) * ${y}))`,
    }
  })

  // What the top and bottom rows lean over: the heading above the wall and the
  // button below it. Measured off the placement rather than guessed at, and
  // given back as a share of the wall's width, which is the unit it is in.
  const rows = Math.ceil(tiles.length / cols)
  const share = (value: number) => `${(value / cols) * 100}%`

  return {
    tiles,
    paddingTop: share(Math.max(0, ...placed.map(box => -box.top))),
    paddingBottom: share(Math.max(0, ...placed.map(box => box.bottom - rows * TILE_HEIGHT))),
  }
})

const openCarousel = (index: number) => {
  activeIndex.value = index
  lightboxView.value = 'carousel'
  lightboxOpen.value = true
}

const openGrid = () => {
  lightboxView.value = 'grid'
  lightboxOpen.value = true
}
</script>

<template>
  <div v-if="media?.length">
    <button
      v-if="hasMore"
      type="button"
      class="text-accent hover:text-black mb-1"
      @click="openGrid"
    >
      {{ $t('labels.seeAllImages', { count: media.length }) }}
    </button>
    
    <!-- No gap: the placement owns all the spacing between images. -->
    <div
      ref="wall"
      class="flex flex-wrap"
      :style="{ paddingTop: layout.paddingTop, paddingBottom: layout.paddingBottom }"
    >
      <div
        v-for="(tile, index) in layout.tiles"
        :key="`${tile.item.id}-${tile.index}`"
        class="relative w-1/3 md:w-1/4 flex flex-col items-end justify-end"
        :style="{ aspectRatio: SLOT_ASPECT }"
      >
        <button
          type="button"
          class="media-preview-tile absolute inset-0 m-auto group !hover:bg-white"
          :style="{ '--tile-transform': tile.transform, width: tile.width, aspectRatio: tile.ratio }"
          @click="openCarousel(tile.index)"
        >
          <core-media-preview-item :media="tile.item" class="group-hover:opacity-75 group-focus:opacity-75 transition-transform" />
        </button>
      </div>
    </div>

    <core-media-preview-lightbox
      v-model:open="lightboxOpen"
      v-model:active-index="activeIndex"
      v-model:view="lightboxView"
      :media="media"
    />
  </div>
</template>

<style scoped>
.media-preview-tile {
  transform: var(--tile-transform);
}
</style>
